import { useEffect, useState } from 'react'
import type { EmailRule, EmailRuleCreate } from '@/types/emailRule'

interface EmailRuleFormProps {
  initialData?: EmailRule
  onSubmit: (data: EmailRuleCreate) => void
  onCancel: () => void
  /** When true, omits outer card styling (for use inside a modal). */
  isModal?: boolean
}

export default function EmailRuleForm({
  initialData,
  onSubmit,
  onCancel,
  isModal = false,
}: EmailRuleFormProps) {
  const [formData, setFormData] = useState<EmailRuleCreate>({
    vendor_name: initialData?.vendor_name || '',
    from_filter: initialData?.from_filter || '',
    subject_contains: initialData?.subject_contains || '',
    email_has_words: initialData?.email_has_words || '',
    has_attachment: initialData?.has_attachment || false,
    destinations: initialData?.destinations || [],
    status: initialData?.status || 'confirmed',
    notes: initialData?.notes || '',
    extraction_directive: initialData?.extraction_directive || '',
    is_negative_rule: initialData?.is_negative_rule || false,
  })

  const [thresholdInput, setThresholdInput] = useState('')

  useEffect(() => {
    if (
      initialData?.amount_alert_threshold != null &&
      String(initialData.amount_alert_threshold).trim() !== ''
    ) {
      setThresholdInput(String(initialData.amount_alert_threshold))
    } else {
      setThresholdInput('')
    }
  }, [initialData?.id, initialData?.amount_alert_threshold])

  const destinations = ['qbo', 'meez']

  function toggleDestination(dest: string) {
    setFormData((prev) => ({
      ...prev,
      destinations: prev.destinations.includes(dest)
        ? prev.destinations.filter((d) => d !== dest)
        : [...prev.destinations, dest],
    }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const t = thresholdInput.trim()
    const payload: EmailRuleCreate = { ...formData }
    if (payload.is_negative_rule) {
      payload.destinations = []
      payload.extraction_directive = null
      payload.amount_alert_threshold = null
    }
    if (initialData && !payload.is_negative_rule) {
      if (t === '') {
        payload.amount_alert_threshold = null
      } else {
        const n = parseFloat(t)
        payload.amount_alert_threshold = Number.isFinite(n) && n >= 0 ? n : null
      }
    } else if (!payload.is_negative_rule && t !== '') {
      const n = parseFloat(t)
      if (Number.isFinite(n) && n >= 0) {
        payload.amount_alert_threshold = n
      }
    }
    onSubmit(payload)
  }

  const cardClass = isModal ? 'space-y-4' : 'card space-y-4'

  return (
    <form onSubmit={handleSubmit} className={cardClass}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="label text-xs">Vendor Name <span className="text-red-500">*</span></label>
          <input
            type="text"
            className="input"
            value={formData.vendor_name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, vendor_name: e.target.value }))
            }
            placeholder="e.g., Sysco Foods"
            required
          />
        </div>

        <div>
          <label className="label text-xs">From Filter</label>
          <input
            type="text"
            className="input"
            value={formData.from_filter || ''}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, from_filter: e.target.value }))
            }
            placeholder="sender@vendor.com"
          />
        </div>

        <div>
          <label className="label text-xs">Subject Contains</label>
          <input
            type="text"
            className="input"
            value={formData.subject_contains || ''}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, subject_contains: e.target.value }))
            }
            placeholder="Invoice"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="label text-xs">Email Has Words</label>
          <input
            type="text"
            className="input"
            value={formData.email_has_words || ''}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email_has_words: e.target.value }))
            }
            placeholder="payment due, amount owed"
          />
        </div>

        <div className="sm:col-span-2 pt-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Matching options
          </h3>
          <div className="space-y-1 mb-2">
            <div className="flex items-center gap-3">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={formData.status === 'confirmed'}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.checked ? 'confirmed' : 'disabled',
                    }))
                  }
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="text-sm text-gray-700">
                {formData.status === 'confirmed' ? 'Status: confirmed' : 'Status: disabled'}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={formData.has_attachment}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, has_attachment: e.target.checked }))
                  }
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="text-sm text-gray-700">Require attachment</span>
            </div>

            <div className="flex items-center gap-3">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={formData.is_negative_rule || false}
                  onChange={(e) => {
                    const checked = e.target.checked
                    if (checked) {
                      setThresholdInput('')
                    }
                    setFormData((prev) => ({
                      ...prev,
                      is_negative_rule: checked,
                      destinations: checked ? [] : prev.destinations,
                      extraction_directive: checked ? '' : prev.extraction_directive,
                    }))
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="text-sm text-gray-700">Negative rule (exclude from processing)</span>
            </div>
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="label text-xs">Notes</label>
          <textarea
            className="input"
            value={formData.notes || ''}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, notes: e.target.value }))
            }
            placeholder="Add any notes about this rule..."
            rows={3}
          />
        </div>

        {!formData.is_negative_rule && (
          <div className="sm:col-span-2">
            <label className="label text-xs">Bill amount alert (optional)</label>
            <input
              type="number"
              min={0}
              step="0.01"
              className="input max-w-xs"
              value={thresholdInput}
              onChange={(e) => setThresholdInput(e.target.value)}
              placeholder="e.g. 500"
            />
            <p className="mt-1 text-[11px] text-gray-500 leading-snug">
              When set, bills ingested through this rule show a reminder in the document details
              modal if the total is above this amount (USD). Leave blank for no alert.
            </p>
          </div>
        )}

        {!formData.is_negative_rule && (
          <div className="sm:col-span-2">
            <label className="label text-xs">PDF extraction hints</label>
            <textarea
              className="input font-mono text-sm"
              value={formData.extraction_directive || ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  extraction_directive: e.target.value,
                }))
              }
              placeholder={
                'Optional. Appended to GPT when parsing OCR from PDFs for this vendor.\n' +
                'Example: Line totals are in the column labeled "Ext"; ignore the "Picked" column.'
              }
              rows={4}
            />
          </div>
        )}
      </div>

      {!formData.is_negative_rule && (
      <div className="mb-2">
        <label className="label text-xs">Destinations</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {destinations.map((dest) => (
            <button
              key={dest}
              type="button"
              onClick={() => toggleDestination(dest)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                formData.destinations.includes(dest)
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {dest.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!formData.vendor_name}
        >
          {initialData ? 'Update' : 'Create'} Rule
        </button>
      </div>
    </form>
  )
}
